import {useCallback} from 'react'

import {downloadDebugLog, formatDebugLog, type AhpDebugEvent} from '../utils/ahp-debug-log'
import {buildBugReportBody, createBugReportIssue} from '../utils/create-bug-report-issue'
import {parseStaffDebugCommand, type StaffDebugCommand} from '../utils/staff-debug-commands'
import {useOptionalDebugEventsGetter} from './use-debug-events'

export interface StaffDebugCommandFeedback {
  variant: 'success' | 'danger'
  message: string
  link?: {href: string; label: string}
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

async function copyToClipboard(text: string): Promise<void> {
  if (!navigator.clipboard?.writeText) throw new Error('The clipboard is not available in this browser.')
  await navigator.clipboard.writeText(text)
}

function openInNewTab(url: string): boolean {
  return Boolean(window.open(url, '_blank', 'noopener,noreferrer'))
}

/**
 * Runs the staff debug slash commands. The returned callback reports whether it
 * consumed the text, so the caller can fall through to a normal chat turn.
 */
export function useStaffDebugCommandDispatch({
  sessionId,
  onFeedback,
  debugEvents,
}: {
  sessionId?: string
  onFeedback: (feedback: StaffDebugCommandFeedback) => void
  debugEvents?: readonly AhpDebugEvent[]
}): (text: string) => boolean {
  const getDebugEvents = useOptionalDebugEventsGetter()
  const getActiveDebugEvents = useCallback(() => debugEvents ?? getDebugEvents(), [debugEvents, getDebugEvents])

  const run = useCallback(
    async (command: StaffDebugCommand) => {
      switch (command.kind) {
        case 'invalid':
          onFeedback({variant: 'danger', message: command.message})
          return
        case 'copy-debug-log':
          try {
            await copyToClipboard(formatDebugLog(getActiveDebugEvents()))
            onFeedback({variant: 'success', message: 'Debug log copied to the clipboard.'})
          } catch (error) {
            onFeedback({variant: 'danger', message: errorMessage(error, 'Could not copy the debug log.')})
          }
          return
        case 'download-debug-log':
          try {
            downloadDebugLog(formatDebugLog(getActiveDebugEvents()))
            onFeedback({variant: 'success', message: 'Debug log downloaded.'})
          } catch (error) {
            onFeedback({variant: 'danger', message: errorMessage(error, 'Could not download the debug log.')})
          }
          return
        case 'report-bug':
          try {
            const url = await createBugReportIssue({
              title: `[Bug] ${command.title}`,
              body: buildBugReportBody({sessionId, debugLog: formatDebugLog(getActiveDebugEvents())}),
            })
            // Creating the issue outlives the user gesture, so the popup
            // blocker may reject the tab. Fall back to a link when it does.
            onFeedback(
              openInNewTab(url)
                ? {variant: 'success', message: 'Bug report created.'}
                : {variant: 'success', message: 'Bug report created.', link: {href: url, label: 'View issue'}},
            )
          } catch (error) {
            onFeedback({variant: 'danger', message: errorMessage(error, 'Could not create the bug report.')})
          }
      }
    },
    [getActiveDebugEvents, onFeedback, sessionId],
  )

  return useCallback(
    (text: string) => {
      const command = parseStaffDebugCommand(text)
      if (!command) return false
      void run(command)
      return true
    },
    [run],
  )
}
